import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  TextField,
  Chip,
  Divider,
  Alert,
  AlertTitle,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DownloadIcon from "@mui/icons-material/Download";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningIcon from "@mui/icons-material/Warning";
import ViewInArIcon from "@mui/icons-material/ViewInAr";
import { useWizard } from "../WizardContext";
import Fade from "@mui/material/Fade";
import { normalizeUrl } from "../../../utils/urlUtils";

// All blocks now use external loaders for optimal size reduction

/**
 * Platform Instructions Component
 * Shows dynamic instructions based on selected platform
 */
const PlatformInstructions = ({ platform, handleCopyCode, handleCopyIframeCode, copySuccess, iframeCopySuccess }) => {
  const [showIframeOption, setShowIframeOption] = React.useState(false);
  const getInstructions = (platform) => {
    switch (platform) {
      case 'wordpress':
        return {
          steps: [
            {
              title: "Access your WordPress dashboard",
              detail: "Go to **Pages** → create a new page or select the page you want to edit."
            },
            {
              title: "Add a Custom HTML block",
              detail: "Click the **+** button to add a new block → search for **\"Custom HTML\"** or **\"HTML\"**."
            },
            {
              title: "Paste your code for your new PC page",
              detail: "Paste the page you've copied into the *HTML content area* → the preview should show a placeholder."
            },
            {
              title: "Publish your changes",
              detail: "Click **Update** or **Publish** → your new PC webpage should now appear on your live website."
            }
          ],
          tip: "If you need help, contact **Support@PartnershipCharter.com**."
        };
      case 'webflow':
        return {
          steps: [
            {
              title: "Open the Webflow Designer",
              detail: "Log into your Webflow account → select your project → open the **Designer**."
            },
            {
              title: "Add an Embed element",
              detail: "From the *Add panel* on the left → **Components** → drag **Embed** to your page."
            },
            {
              title: "Insert your code",
              detail: "Double-click the embed element → paste your code in the **HTML Embed** settings panel."
            },
            {
              title: "Publish to make it live",
              detail: "Click **Publish** in the top-right corner → select your domain → publish changes."
            }
          ],
          tip: "**Important:** You must publish your site for changes to appear. The Designer preview may not show the final result."
        };
      case 'squarespace':
        return {
          steps: [
            {
              title: "Edit your page",
              detail: "Navigate to the page you want to edit → click **Edit** at the top."
            },
            {
              title: "Add a Code Block",
              detail: "Click **+** to add content → **More** → select **Code** (not Markdown)."
            },
            {
              title: "Paste and apply",
              detail: "Paste your code into the code editor → click **Apply** to save."
            },
            {
              title: "Save your page",
              detail: "Click **Save** in the top-left → your new PC webpage should now appear on your live website."
            }
          ],
          tip: "Make sure to use a **Code Block**, not a Markdown block. The code block has a `</>` icon."
        };
      case 'general':
      default:
        return {
          steps: [
            {
              title: "Access your website editor",
              detail: "Log into your website's *admin panel* or *content management system*."
            },
            {
              title: "Find the HTML/Embed option",
              detail: "Look for options labeled **\"Custom HTML\"**, **\"Embed Code\"**, **\"HTML Block\"**, or **\"Custom Code\"**."
            },
            {
              title: "Paste your code",
              detail: "Paste the code into the *HTML field* → save or apply changes."
            },
            {
              title: "Publish your changes",
              detail: "Save and **publish** your page → your new PC webpage should now appear on your live website."
            }
          ],
          tip: "Common terms to look for: *Custom HTML*, *Embed*, *Code Block*, *HTML Widget*, or *Raw HTML*."
        };
    }
  };

  const instructions = getInstructions(platform);

  // Helper function to render text with bold and italic markdown
  const renderFormattedText = (text) => {
    const parts = [];
    let currentIndex = 0;
    
    // Find all bold (**text**) and italic (*text*) patterns
    const regex = /(\*\*([^*]+)\*\*)|(\*([^*]+)\*)/g;
    let match;
    
    while ((match = regex.exec(text)) !== null) {
      // Add text before the match
      if (match.index > currentIndex) {
        parts.push(text.slice(currentIndex, match.index));
      }
      
      // Add the formatted text
      if (match[1]) { // Bold text
        parts.push(<strong key={match.index}>{match[2]}</strong>);
      } else if (match[3]) { // Italic text
        parts.push(<em key={match.index} style={{ fontStyle: 'italic', color: '#666' }}>{match[4]}</em>);
      }
      
      currentIndex = regex.lastIndex;
    }
    
    // Add remaining text
    if (currentIndex < text.length) {
      parts.push(text.slice(currentIndex));
    }
    
    return parts.length > 0 ? parts : text;
  };

  return (
    <Box>
      <Grid container spacing={3}>
        {/* Step 1: Copy buttons */}
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2.5 }}>
            <Box sx={{
              minWidth: 36,
              height: 36,
              borderRadius: '50%',
              bgcolor: '#1C436A',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontWeight: 'bold',
              fontSize: '0.875rem',
              flexShrink: 0
            }}>
              2
            </Box>
            <Box sx={{ flex: 1, pt: 0.5 }}>
              <Typography variant="body1" sx={{ fontWeight: '600', color: '#1C436A', mb: 1 }}>
                Copy your PC webpage code
              </Typography>
              <Typography variant="body2" sx={{ color: '#555', lineHeight: 1.6, mb: 2 }}>
                Click the button below to copy your PC webpage code to your clipboard.
              </Typography>

              {!showIframeOption ? (
                <>
                  {/* Default: Show Website Code button */}
                  <Button
                    variant="contained"
                    size="medium"
                    onClick={handleCopyCode}
                    startIcon={<ContentCopyIcon />}
                    sx={{
                      bgcolor: "#CE950F",
                      "&:hover": { bgcolor: "#B5850D" },
                      px: 3,
                      py: 1,
                      fontSize: "0.95rem",
                      fontWeight: "bold",
                      textTransform: "none",
                      mb: 2
                    }}
                  >
                    Copy Website Code
                  </Button>

                  {/* Success Message for Website Code */}
                  {copySuccess && (
                    <Fade in timeout={300}>
                      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CheckCircleIcon sx={{ color: '#4CAF50', fontSize: 18 }} />
                        <Typography variant="body2" sx={{ color: '#4CAF50', fontWeight: 'medium' }}>
                          Code copied! Ready to paste into your website.
                        </Typography>
                      </Box>
                    </Fade>
                  )}

                  {/* Alternative option link */}
                  <Typography variant="body2" sx={{ color: '#666', fontSize: '0.875rem' }}>
                    If it doesn't work when you paste it in your website (step 4 below), try this{' '}
                    <Typography
                      component="span"
                      sx={{
                        color: '#1976d2',
                        cursor: 'pointer',
                        textDecoration: 'underline',
                        '&:hover': { color: '#1565c0' }
                      }}
                      onClick={() => setShowIframeOption(true)}
                    >
                      iframe
                    </Typography>
                    {' '}version instead.
                  </Typography>
                </>
              ) : (
                <>
                  {/* Alternative: Show Iframe Code button */}
                  <Typography variant="body2" sx={{ color: '#666', mb: 2, fontStyle: 'italic' }}>
                    Using iframe version (works with most website configurations):
                  </Typography>

                  <Button
                    variant="contained"
                    size="medium"
                    onClick={handleCopyIframeCode}
                    startIcon={<ViewInArIcon />}
                    sx={{
                      bgcolor: "#CE950F",
                      "&:hover": { bgcolor: "#B5850D" },
                      px: 3,
                      py: 1,
                      fontSize: "0.95rem",
                      fontWeight: "bold",
                      textTransform: "none",
                      mb: 2
                    }}
                  >
                    Copy Iframe Code
                  </Button>

                  {/* Success Message for Iframe Code */}
                  {iframeCopySuccess && (
                    <Fade in timeout={300}>
                      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CheckCircleIcon sx={{ color: '#4CAF50', fontSize: 18 }} />
                        <Typography variant="body2" sx={{ color: '#4CAF50', fontWeight: 'medium' }}>
                          Iframe code copied! Ready to paste into your website.
                        </Typography>
                      </Box>
                    </Fade>
                  )}

                  {/* Back to website code option */}
                  <Typography variant="body2" sx={{ color: '#666', fontSize: '0.875rem' }}>
                    <Typography
                      component="span"
                      sx={{
                        color: '#1976d2',
                        cursor: 'pointer',
                        textDecoration: 'underline',
                        '&:hover': { color: '#1565c0' }
                      }}
                      onClick={() => setShowIframeOption(false)}
                    >
                      ← Back to website code
                    </Typography>
                  </Typography>
                </>
              )}
            </Box>
          </Box>
        </Grid>

        {/* Remaining steps */}
        {instructions.steps.map((step, index) => (
          <Grid item xs={12} key={index}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2.5 }}>
              <Box sx={{
                minWidth: 36,
                height: 36,
                borderRadius: '50%',
                bgcolor: '#1C436A',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontWeight: 'bold',
                fontSize: '0.875rem',
                flexShrink: 0
              }}>
                {index + 3}
              </Box>
              <Box sx={{ flex: 1, pt: 0.5 }}>
                <Typography variant="body1" sx={{ fontWeight: '600', color: '#1C436A', mb: 0.5 }}>
                  {step.title}
                </Typography>
                <Typography variant="body2" sx={{ color: '#555', lineHeight: 1.6 }}>
                  {renderFormattedText(step.detail)}
                </Typography>
              </Box>
            </Box>
          </Grid>
        ))}
      </Grid>
      
      {instructions.tip && (
        <Box sx={{ 
          mt: 4, 
          p: 2.5, 
          bgcolor: '#f0f7ff', 
          borderRadius: 2, 
          borderLeft: '4px solid #1976D2',
          border: '1px solid #e3f2fd'
        }}>
          <Typography variant="body2" sx={{ color: '#1565c0', lineHeight: 1.6 }}>
            <strong>💡 Tip:</strong> {renderFormattedText(instructions.tip)}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

/**
 * Step 3: Review and Export Component
 * Shows final preview and provides embed code for copying
 */
const ReviewExport = () => {
  const { 
    selectedBlocks, 
    globalStyles, 
    logoUpload,
    resetWizard 
  } = useWizard();

  const [tabValue, setTabValue] = useState(0);
  const [copySuccess, setCopySuccess] = useState(false);
  const [iframeCopySuccess, setIframeCopySuccess] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState('wordpress');

  // Generate individual embed codes for each selected block
  const generateEmbedCode = () => {
    const blockList = selectedBlocks.map(block => block.id).join(", ");
    
    // Use the logo from globalStyles, which contains the uploaded URL when available
    const logoUrl = globalStyles.companyLogo;
    
    // Create unified config that matches what the preview components receive
    const createBlockConfig = (blockSpecificConfig = {}) => {
      // Normalize buttonUrl and contactPageUrl to ensure they have https://
      const normalizedButtonUrl = normalizeUrl(globalStyles.contactPageUrl || blockSpecificConfig.buttonUrl || '');

      return {
        // Global styles that apply to all blocks
        backgroundColor: globalStyles.backgroundColor,
        textColor: globalStyles.textColor,
        headingColor: globalStyles.primaryColor,
        primaryColor: globalStyles.primaryColor,
        accentColor: globalStyles.accentColor,
        headingFont: globalStyles.headingFont,
        bodyFont: globalStyles.bodyFont,
        companyName: globalStyles.companyName,
        companyAcronym: globalStyles.companyAcronym,
        logoUrl: logoUrl,
        // Normalize contact/button URLs
        buttonUrl: normalizedButtonUrl,
        contactPageUrl: normalizedButtonUrl,
        // Block-specific overrides
        ...blockSpecificConfig,
        // Ensure normalized URL is used even if block-specific config has buttonUrl
        ...(normalizedButtonUrl && { buttonUrl: normalizedButtonUrl, contactPageUrl: normalizedButtonUrl })
      };
    };
    
    // Create block embed code generators mapping
    const blockGenerators = {
      hero: () => {
        const heroConfig = createBlockConfig({
          backgroundImageUrl: "https://tpci.vercel.app/hero_bg.png",
          overlayColor: "rgba(28, 67, 106, 0.7)",
          textColor: "#ffffff",
          taglineText: "...never operate a partnership without one",
          height: "50vh"  // Using viewport height for WordPress compatibility
        });
        
        return `<script 
  src="https://tpci.vercel.app/embed/loaders/hero-loader.js"
  data-background-image="${heroConfig.backgroundImageUrl}"
  data-overlay-color="${heroConfig.overlayColor}"
  data-text-color="${heroConfig.textColor}"
  data-heading-font="${heroConfig.headingFont}"
  data-tagline-text="${heroConfig.taglineText}"
  data-logo-url="${heroConfig.logoUrl}"
  data-company-acronym="${heroConfig.companyAcronym}"
  data-height="${heroConfig.height}">
</script>`;
      },
      
      
      charter: () => {
        const charterConfig = createBlockConfig({
          backgroundColor: globalStyles.primaryColor,
          textColor: "#ffffff",
          headingColor: "#ffffff",
          logoSrc: "https://tpci.vercel.app/tpci_logo.svg",
          imageSrc: "https://tpci.vercel.app/chartersection_computer.jpg"
        });
        
        return `<script 
  src="https://tpci.vercel.app/embed/loaders/charter-section-loader.js"
  data-background="${charterConfig.backgroundColor}"
  data-text-color="${charterConfig.textColor}"
  data-heading-color="${charterConfig.headingColor}"
  data-heading-font="${charterConfig.headingFont}"
  data-body-font="${charterConfig.bodyFont}"
  data-logo-src="${charterConfig.logoSrc}"
  data-image-src="${charterConfig.imageSrc}"
  data-image-max-height="350"
  data-image-max-width="500">
</script>`;
      },
      
      charterTypes: () => {
        // Use compact external loader to reduce embed code size
        const charterTypesConfig = createBlockConfig({
          backgroundColor: "#ffffff",
          textColor: globalStyles.primaryColor
        });
        
        return `<script 
  src="https://tpci.vercel.app/embed/loaders/charter-types-compact.js"
  data-background-color="${charterTypesConfig.backgroundColor}"
  data-text-color="${charterTypesConfig.textColor}"
  data-primary-color="${charterTypesConfig.primaryColor}"
  data-accent-color="${charterTypesConfig.accentColor}"
  data-heading-color="${charterTypesConfig.headingColor}"
  data-heading-font="${charterTypesConfig.headingFont}"
  data-body-font="${charterTypesConfig.bodyFont}"
  data-company-name="${charterTypesConfig.companyName}"
  data-company-acronym="${charterTypesConfig.companyAcronym}"
  data-logo-url="${charterTypesConfig.logoUrl}">
</script>`;
      },
      
      threeSteps: () => {
        const threeStepsConfig = createBlockConfig({
          accentColor: globalStyles.accentColor
        });
        
        return `<style>
/* NUCLEAR OPTION: Force spacing with maximum specificity */

@media (max-width: 1024px) {
  /* Override container restrictions */
  .three-steps-container .three-steps-wrapper {
    max-width: none !important;
    width: 100% !important;
    margin: 0 !important;
    padding: 0 !important;
  }
  
  /* Force single column layout and remove constraints */
  .three-steps-container .three-steps-grid {
    display: block !important;
    max-width: none !important;
    margin: 0 !important;
    padding: 0 !important;
    gap: 0 !important;
    grid-gap: 0 !important;
  }
  
  /* Force spacing between cards with explicit margins */
  .three-steps-container .three-steps-grid .step-card {
    display: block !important;
    margin-bottom: 50px !important;
    margin-top: 0px !important;
    width: 100% !important;
    max-width: 500px !important;
    margin-left: auto !important;
    margin-right: auto !important;
    box-sizing: border-box !important;
    height: 300px !important;
    min-height: 300px !important;
  }
  
  /* Remove margin from last card */
  .three-steps-container .three-steps-grid .step-card:last-child {
    margin-bottom: 0px !important;
  }
  
  /* Triple specificity for stubborn cases */
  div.three-steps-container div.three-steps-grid div.step-card {
    margin-bottom: 50px !important;
  }
  
  div.three-steps-container div.three-steps-grid div.step-card:last-child {
    margin-bottom: 0px !important;
  }
}

@media (max-width: 768px) {
  .three-steps-container .three-steps-wrapper {
    max-width: none !important;
    margin: 0 !important;
    padding: 0 !important;
  }
  
  .three-steps-container .three-steps-grid {
    max-width: none !important;
    margin: 0 !important;
    padding: 0 !important;
    gap: 0 !important;
  }
  
  .three-steps-container .three-steps-grid .step-card {
    margin-bottom: 45px !important;
    height: 300px !important;
    min-height: 300px !important;
  }
  
  div.three-steps-container div.three-steps-grid div.step-card {
    margin-bottom: 45px !important;
  }
}

@media (max-width: 480px) {
  .three-steps-container .three-steps-wrapper {
    max-width: none !important;
    margin: 0 !important;
    padding: 0 !important;
  }
  
  .three-steps-container .three-steps-grid {
    max-width: none !important;
    margin: 0 !important;
    padding: 0 !important;
    gap: 0 !important;
  }
  
  .three-steps-container .three-steps-grid .step-card {
    margin-bottom: 40px !important;
    height: 300px !important;
    min-height: 300px !important;
  }
  
  div.three-steps-container div.three-steps-grid div.step-card {
    margin-bottom: 40px !important;
  }
  
  .three-steps-container {
    padding-left: 1rem !important;
    padding-right: 1rem !important;
  }
}
</style>

<script 
  src="https://tpci.vercel.app/embed/loaders/three-steps-loader.js"
  data-background="${threeStepsConfig.backgroundColor}"
  data-text-color="${threeStepsConfig.textColor}"
  data-heading-color="${threeStepsConfig.headingColor}"
  data-accent-color="${threeStepsConfig.accentColor}"
  data-badge-color="${threeStepsConfig.primaryColor}"
  data-badge-text-color="#ffffff"
  data-heading-font="${threeStepsConfig.headingFont}"
  data-body-font="${threeStepsConfig.bodyFont}">
</script>`;
      },
      
      dypTool: () => {
        const dypToolConfig = createBlockConfig({
          imageSrc: "https://tpci.vercel.app/dyp_tool.png",
          imageRadius: 8,
          buttonColor: globalStyles.accentColor,
          buttonRadius: 8,
          buttonText: `Contact ${globalStyles.companyAcronym}`
        });
        
        return `<script 
  src="https://tpci.vercel.app/embed/loaders/dyp-tool-loader.js"
  data-background-color="${dypToolConfig.backgroundColor}"
  data-text-color="${dypToolConfig.textColor}"
  data-heading-color="${dypToolConfig.headingColor}"
  data-heading-font="${dypToolConfig.headingFont}"
  data-body-font="${dypToolConfig.bodyFont}"
  data-image-src="${dypToolConfig.imageSrc}"
  data-image-radius="${dypToolConfig.imageRadius}"
  data-button-color="${dypToolConfig.buttonColor}"
  data-button-radius="${dypToolConfig.buttonRadius}"
  data-button-text="${dypToolConfig.buttonText}">
</script>`;
      },
      
      partners: () => {
        const partnersConfig = createBlockConfig({
          imageRadius: 8
        });
        
        return `<script 
  src="https://tpci.vercel.app/embed/loaders/partners-benefits-loader.js"
  data-background-color="${partnersConfig.backgroundColor}"
  data-text-color="${partnersConfig.textColor}"
  data-heading-color="${partnersConfig.headingColor}"
  data-accent-color="${partnersConfig.accentColor}"
  data-heading-font="${partnersConfig.headingFont}"
  data-body-font="${partnersConfig.bodyFont}"
  data-image-radius="${partnersConfig.imageRadius}">
</script>`;
      },
      
      pcProcess: () => {
        // Use compact external loader to reduce embed code size
        const pcProcessConfig = createBlockConfig({
          buttonText: `Contact ${globalStyles.companyAcronym}`,
          buttonColor: globalStyles.accentColor,
          buttonUrl: globalStyles.contactPageUrl || "#",
          transitionSpeed: 300,
          accordionRadius: 8
        });
        
        return `<script 
  src="https://tpci.vercel.app/embed/loaders/pc-process-compact.js"
  data-background-color="${pcProcessConfig.backgroundColor}"
  data-text-color="${pcProcessConfig.textColor}"
  data-primary-color="${pcProcessConfig.primaryColor}"
  data-accent-color="${pcProcessConfig.accentColor}"
  data-heading-color="${pcProcessConfig.headingColor}"
  data-heading-font="${pcProcessConfig.headingFont}"
  data-body-font="${pcProcessConfig.bodyFont}"
  data-company-name="${pcProcessConfig.companyName}"
  data-company-acronym="${pcProcessConfig.companyAcronym}"
  data-button-color="${pcProcessConfig.buttonColor}"
  data-button-text="${pcProcessConfig.buttonText}"
  data-button-url="${pcProcessConfig.buttonUrl}">
</script>`;
      },
      
      dypAdvantages: () => {
        const dypAdvantagesConfig = createBlockConfig({
          backgroundColor: globalStyles.primaryColor,
          textColor: "#ffffff",
          headingColor: "#ffffff",
          transitionSpeed: 0.3,
          accordionRadius: 8,
          buttonColor: globalStyles.accentColor,
          buttonRadius: 8,
          buttonText: `Contact ${globalStyles.companyAcronym || 'Us'}`,
          buttonUrl: globalStyles.contactPageUrl || "#"
        });
        
        return `<script 
  src="https://tpci.vercel.app/embed/loaders/dyp-advantages-loader.js"
  data-background-color="${dypAdvantagesConfig.backgroundColor}"
  data-text-color="${dypAdvantagesConfig.textColor}"
  data-heading-color="${dypAdvantagesConfig.headingColor}"
  data-accent-color="${dypAdvantagesConfig.accentColor}"
  data-heading-font="${dypAdvantagesConfig.headingFont}"
  data-body-font="${dypAdvantagesConfig.bodyFont}"
  data-accordion-radius="${dypAdvantagesConfig.accordionRadius}"
  data-button-color="${dypAdvantagesConfig.buttonColor}"
  data-button-radius="${dypAdvantagesConfig.buttonRadius}"
  data-button-text="${dypAdvantagesConfig.buttonText}"
  data-button-url="${dypAdvantagesConfig.buttonUrl}"
  data-transition-speed="${dypAdvantagesConfig.transitionSpeed}">
</script>`;
      },
      
      assistanceLevels: () => {
        const assistanceConfig = createBlockConfig({
          imageSrc: "https://tpci.vercel.app/assistance_levels_diagram.png",
          imageRadius: 8
        });
        
        return `<script 
  src="https://tpci.vercel.app/embed/loaders/assistance-levels-loader.js"
  data-background="${assistanceConfig.backgroundColor}"
  data-text-color="${assistanceConfig.textColor}"
  data-heading-color="${assistanceConfig.headingColor}"
  data-heading-font="${assistanceConfig.headingFont}"
  data-body-font="${assistanceConfig.bodyFont}"
  data-image-src="${assistanceConfig.imageSrc}"
  data-image-radius="${assistanceConfig.imageRadius}">
</script>`;
      },
      
      bookPromo: () => {
        const bookPromoConfig = createBlockConfig({
          textColor: "#ffffff",
          bookImageSrc: "https://tpci.vercel.app/book_partnership_charter.png",
          amazonLogoSrc: "https://tpci.vercel.app/logo_amazon.png",
          barnesNobleLogoSrc: "https://tpci.vercel.app/logo_barnes_noble.png",
          indieBoundLogoSrc: "https://tpci.vercel.app/logo_indie_bound.png",
          googleLogoSrc: "https://tpci.vercel.app/logo_google_play.png",
          audibleLogoSrc: "https://tpci.vercel.app/logo_audible.png",
          logoInvert: false
        });
        
        return `<script 
  src="https://tpci.vercel.app/embed/loaders/book-promo-loader.js"
  data-background-color="${bookPromoConfig.accentColor}"
  data-text-color="${bookPromoConfig.textColor}"
  data-primary-color="${bookPromoConfig.primaryColor}"
  data-heading-font="${bookPromoConfig.headingFont}"
  data-body-font="${bookPromoConfig.bodyFont}"
  data-book-image="${bookPromoConfig.bookImageSrc}"
  data-amazon-logo="${bookPromoConfig.amazonLogoSrc}"
  data-barnes-logo="${bookPromoConfig.barnesNobleLogoSrc}"
  data-indie-logo="${bookPromoConfig.indieBoundLogoSrc}"
  data-google-logo="${bookPromoConfig.googleLogoSrc}"
  data-audible-logo="${bookPromoConfig.audibleLogoSrc}"
  data-invert-logos="${bookPromoConfig.logoInvert}"
  data-contact-link="mailto:Support@PartnershipCharter.com?subject=I'd like to place a bulk order for The Partnership Charter book">
</script>`;
      },
      
      testimonials: () => {
        const testimonialsConfig = createBlockConfig({
          backgroundColor: globalStyles.primaryColor,
          textColor: "#ffffff",
          testimonialImage: "https://tpci.vercel.app/testimonial_photo.jpeg"
        });
        
        return `<script 
  src="https://tpci.vercel.app/embed/loaders/testimonials-loader.js"
  data-background="${testimonialsConfig.backgroundColor}"
  data-text-color="${testimonialsConfig.textColor}"
  data-heading-font="${testimonialsConfig.headingFont}"
  data-body-font="${testimonialsConfig.bodyFont}"
  data-testimonial-image="${testimonialsConfig.testimonialImage}">
</script>`;
      },
      
      sliversOfAmbiguity: () => {
        const sliversConfig = createBlockConfig({
          accentColor: globalStyles.accentColor
        });
        
        return `<script 
  src="https://tpci.vercel.app/embed/loaders/slivers-loader.js"
  data-background="${sliversConfig.backgroundColor}"
  data-text-color="${sliversConfig.textColor}"
  data-heading-color="${sliversConfig.headingColor}"
  data-accent-color="${sliversConfig.accentColor}"
  data-heading-font="${sliversConfig.headingFont}"
  data-body-font="${sliversConfig.bodyFont}">
</script>`;
      },
      
      dypLicensedUser: () => {
        const dypLicensedConfig = createBlockConfig({
          backgroundColor: globalStyles.primaryColor,
          textColor: "#ffffff",
          buttonColor: globalStyles.accentColor,
          buttonText: `Contact ${globalStyles.companyAcronym}`,
          buttonRadius: 8,
          buttonUrl: globalStyles.contactPageUrl || "#",
          logoUrl: "https://tpci.vercel.app/dyp_logo.png",
          organizationName: globalStyles.companyName
        });
        
        return `<script 
  src="https://tpci.vercel.app/embed/loaders/dyp-licensed-user-loader.js"
  data-background="${dypLicensedConfig.backgroundColor}"
  data-text-color="${dypLicensedConfig.textColor}"
  data-accent-color="${dypLicensedConfig.buttonColor}"
  data-heading-font="${dypLicensedConfig.headingFont}"
  data-body-font="${dypLicensedConfig.bodyFont}"
  data-button-radius="${dypLicensedConfig.buttonRadius}"
  data-logo-url="${dypLicensedConfig.logoUrl}"
  data-organization-name="${dypLicensedConfig.organizationName}"
  data-button-text="${dypLicensedConfig.buttonText}"
  data-button-url="${dypLicensedConfig.buttonUrl}">
</script>`;
      }
    };
    
    // Generate individual script tags for each selected block
    const embedCodes = selectedBlocks.map(block => {
      const generator = blockGenerators[block.id];
      if (!generator) {
        console.warn(`No embed code generator found for block: ${block.id}`);
        return `<!-- Missing generator for block: ${block.id} -->`;
      }
      return generator();
    });
    
    // Combine all embed codes
    const combinedCode = embedCodes.join('\n\n');
    
    // WordPress compatibility is now handled inside each loader's shadow DOM
    
    return `<!-- Generated by Block Code Generator -->
<!-- Selected Blocks: ${blockList} -->
<!-- Company: ${globalStyles.companyName} -->
<!-- Primary Color: ${globalStyles.primaryColor} -->
<!-- Accent Color: ${globalStyles.accentColor} -->

${combinedCode}`;
  };

  const embedCode = generateEmbedCode();

  // Validate embed code
  const validateEmbedCode = () => {
    const issues = [];
    
    // Check if blocks are selected
    if (selectedBlocks.length === 0) {
      issues.push("No blocks selected");
    }
    
    // Check required fields
    if (!globalStyles.companyName.trim()) {
      issues.push("Company name is required");
    }
    
    // Check if logo URL is valid (either blob URL or default)
    const logoUrl = (logoUpload && logoUpload.blobUrl) || globalStyles.companyLogo;
    if (!logoUrl) {
      issues.push("Logo URL is missing");
    }
    
    // Validate that all selected blocks have corresponding generators
    const availableGenerators = [
      'hero', 'charter', 'charterTypes', 'threeSteps', 'dypTool',
      'partners', 'pcProcess', 'dypAdvantages', 'assistanceLevels', 
      'bookPromo', 'testimonials', 'sliversOfAmbiguity', 'dypLicensedUser'
    ];
    
    const missingGenerators = selectedBlocks.filter(block => 
      !availableGenerators.includes(block.id)
    );
    
    if (missingGenerators.length > 0) {
      issues.push(`Missing embed generators for blocks: ${missingGenerators.map(b => b.name).join(', ')}`);
    }
    
    return {
      isValid: issues.length === 0,
      issues: issues
    };
  };

  const validation = validateEmbedCode();

  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText(embedCode);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 3000);
    } catch (error) {
      console.error("Failed to copy:", error);
    }
  };

  const handleDownload = () => {
    const element = document.createElement("a");
    const file = new Blob([embedCode], { type: "text/html" });
    element.href = URL.createObjectURL(file);
    element.download = `${globalStyles.companyName.toLowerCase().replace(/\s+/g, '-')}-embed-code.html`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  // Generate iframe embed code
  const generateIframeEmbedCode = () => {
    const blockList = selectedBlocks.map(block => block.id).join(',');
    
    // Encode parameters for URL
    const params = new URLSearchParams({
      blocks: blockList,
      companyName: globalStyles.companyName,
      companyAcronym: globalStyles.companyAcronym,
      primaryColor: globalStyles.primaryColor,
      accentColor: globalStyles.accentColor,
      textColor: globalStyles.textColor,
      backgroundColor: globalStyles.backgroundColor,
      headingFont: globalStyles.headingFont,
      bodyFont: globalStyles.bodyFont,
      ...(globalStyles.companyLogo && { logoUrl: globalStyles.companyLogo })
    });

    // Use local URL for development, production URL for production
    const baseUrl = process.env.NODE_ENV === 'development' 
      ? 'http://localhost:3000' 
      : 'https://tpci.vercel.app';
    const iframeUrl = `${baseUrl}/embed/iframe/?${params.toString()}`;
    
    return `<iframe 
  src="${iframeUrl}" 
  width="100%" 
  height="600" 
  frameborder="0" 
  style="border: none; display: block; margin: 0 auto;"
  id="pc-content-iframe">
</iframe>
<script>
// Auto-resize iframe based on content
window.addEventListener('message', function(e) {
  if (e.origin === '${baseUrl}' && e.data.type === 'resize') {
    var iframe = document.getElementById('pc-content-iframe');
    if (iframe) {
      iframe.style.height = e.data.height + 'px';
    }
  }
});
</script>`;
  };

  const iframeEmbedCode = generateIframeEmbedCode();

  const handleCopyIframeCode = async () => {
    try {
      await navigator.clipboard.writeText(iframeEmbedCode);
      setIframeCopySuccess(true);
      setTimeout(() => setIframeCopySuccess(false), 3000);
    } catch (error) {
      console.error("Failed to copy iframe code:", error);
    }
  };

  // Check if we're in development mode to show technical details
  const isDevelopment = process.env.NODE_ENV === 'development';

  return (
    <Box sx={{ 
      minHeight: "60vh",
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      py: { xs: 2, md: 4 }
    }}>
      <Fade in timeout={600}>
        <Paper 
          elevation={3} 
          sx={{ 
            maxWidth: 650,
            width: '100%',
            p: { xs: 3, sm: 4, md: 5 },
            borderRadius: 3,
            textAlign: 'center',
            background: 'linear-gradient(to bottom, #ffffff 0%, #fafbfc 100%)'
          }}
        >
          {/* Success Status */}
          <Box sx={{ mb: 3 }}>
            <Box 
              sx={{ 
                width: 80,
                height: 80,
                borderRadius: '50%',
                bgcolor: '#4CAF50',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2,
                animation: 'successPulse 2s ease-in-out infinite',
                '@keyframes successPulse': {
                  '0%': { transform: 'scale(1)', opacity: 1 },
                  '50%': { transform: 'scale(1.05)', opacity: 0.9 },
                  '100%': { transform: 'scale(1)', opacity: 1 }
                }
              }}
            >
              <CheckCircleIcon sx={{ color: 'white', fontSize: 48 }} />
            </Box>
            <Typography 
              variant="h4" 
              gutterBottom 
              sx={{ 
                color: '#1C436A',
                fontWeight: 'bold',
                fontSize: { xs: '1.5rem', md: '2rem' }
              }}
            >
              Your PC Webpage is Ready to Add to Your Firm's Website!
            </Typography>
          </Box>

          {/* Need Help Section - Hidden for now */}
          {/* <Paper
            elevation={2}
            sx={{
              p: { xs: 2, md: 3 },
              mb: 3,
              borderRadius: 2,
              bgcolor: "#f8f9fc",
              textAlign: "center",
            }}
          >
            <Typography
              variant="h5"
              component="h2"
              gutterBottom
              sx={{ color: "#1e4164", fontWeight: "bold" }}
            >
              Need Help?
            </Typography>
            <Typography
              variant="body1"
              sx={{ mb: 2, maxWidth: "600px", mx: "auto" }}
            >
              Let us know if you have any questions about using this tool or copying
              the blocks
              <br />
              to your website. Our support team is here to help.
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "#1976d2",
                fontWeight: "medium",
                cursor: "pointer",
                "&:hover": { textDecoration: "underline" },
              }}
              onClick={() =>
                navigator.clipboard.writeText("Support@PartnershipCharter.com")
              }
            >
              Support@PartnershipCharter.com
            </Typography>
          </Paper> */}

          {/* Platform-Specific Instructions */}
          <Box sx={{ textAlign: 'left' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2, color: '#1C436A', textAlign: 'center' }}>
             Six Steps To Add a PC Page to Your Website
            </Typography>
            
            {/* Platform Selector */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2.5, mb: 3 }}>
              <Box sx={{ 
                minWidth: 36,
                height: 36,
                borderRadius: '50%',
                bgcolor: '#1C436A',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontWeight: 'bold',
                fontSize: '0.875rem',
                flexShrink: 0
              }}>
                1
              </Box>
              <Box sx={{ flex: 1, pt: 0.5 }}>
                <Typography variant="body1" sx={{ fontWeight: '600', color: '#1C436A', mb: 1 }}>
                  Select your website platform
                </Typography>
                <FormControl sx={{ minWidth: 280 }}>
              <Select
                value={selectedPlatform}
                onChange={(e) => setSelectedPlatform(e.target.value)}
                sx={{
                  '& .MuiSelect-select': {
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }
                }}
              >
                <MenuItem value="general">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ 
                      width: 20, 
                      height: 20, 
                      borderRadius: 1,
                      bgcolor: '#f5f5f5',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      🌐
                    </Box>
                    General Instructions
                  </Box>
                </MenuItem>
                <MenuItem value="wordpress">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ 
                      width: 20, 
                      height: 20, 
                      borderRadius: 1,
                      bgcolor: '#21759B',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Typography sx={{ color: 'white', fontSize: '0.7rem', fontWeight: 'bold' }}>W</Typography>
                    </Box>
                    WordPress
                  </Box>
                </MenuItem>
                <MenuItem value="webflow">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ 
                      width: 20, 
                      height: 20, 
                      borderRadius: 1,
                      bgcolor: '#4353FF',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Typography sx={{ color: 'white', fontSize: '0.7rem', fontWeight: 'bold' }}>W</Typography>
                    </Box>
                    Webflow
                  </Box>
                </MenuItem>
                <MenuItem value="squarespace">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ 
                      width: 20, 
                      height: 20, 
                      borderRadius: 1,
                      bgcolor: '#000000',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Typography sx={{ color: 'white', fontSize: '0.7rem', fontWeight: 'bold' }}>S</Typography>
                    </Box>
                    Squarespace
                  </Box>
                </MenuItem>
                </Select>
                </FormControl>
              </Box>
            </Box>

            {/* Dynamic Instructions Based on Platform */}
            <PlatformInstructions
              platform={selectedPlatform}
              handleCopyCode={handleCopyCode}
              handleCopyIframeCode={handleCopyIframeCode}
              copySuccess={copySuccess}
              iframeCopySuccess={iframeCopySuccess}
            />
          </Box>

          {/* Development Mode: Show Technical Details */}
          {isDevelopment && (
            <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid #e0e0e0' }}>
              {/* Buttons hidden - functionality moved to Step 1 */}
              {/* <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => setTabValue(tabValue === 0 ? 1 : 0)}
                  size="small"
                  sx={{ borderColor: '#CE950F', color: '#CE950F', '&:hover': { borderColor: '#B5850D' } }}
                >
                  {tabValue === 0 ? 'Show' : 'Hide'} Embed Code
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => setTabValue(tabValue === 0 ? 2 : 0)}
                  size="small"
                  sx={{ borderColor: '#1C436A', color: '#1C436A', '&:hover': { borderColor: '#0F2A4A' } }}
                >
                  {tabValue === 0 ? 'Show' : 'Hide'} Iframe Code
                </Button>
              </Box> */}
              
              {tabValue === 1 && (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: '#CE950F', fontWeight: 'bold' }}>
                    JavaScript Embed Code (Current Method)
                  </Typography>
                  <TextField
                    multiline
                    rows={6}
                    fullWidth
                    value={embedCode}
                    InputProps={{ 
                      readOnly: true,
                      sx: { fontFamily: "monospace", fontSize: "0.75rem" }
                    }}
                    sx={{ mt: 1 }}
                  />
                </Box>
              )}

              {tabValue === 2 && (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: '#1C436A', fontWeight: 'bold' }}>
                    Iframe Embed Code (WordPress Recommended)
                  </Typography>
                  <TextField
                    multiline
                    rows={4}
                    fullWidth
                    value={iframeEmbedCode}
                    InputProps={{ 
                      readOnly: true,
                      sx: { fontFamily: "monospace", fontSize: "0.75rem" }
                    }}
                    sx={{ mt: 1 }}
                  />
                </Box>
              )}
            </Box>
          )}
        </Paper>
      </Fade>
    </Box>
  );
};

export default ReviewExport;